/**
 * FreeDeliveryDialog — "دلفري حر"
 * Anyone (guest or logged-in) can request an ad-hoc delivery. All active
 * delivery drivers get an instant push notification and can accept from
 * their dashboard.
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Bike, Navigation, Loader2, CheckCircle2, MapPin, User as UserIcon, Phone, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const STORAGE_KEY = "kh_my_free_deliveries";
const MAX_IMG_BYTES = 800 * 1024;

function saveMyRequestId(id) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    if (!list.includes(id)) list.unshift(id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, 100)));
  } catch { /* ignore */ }
}

export default function FreeDeliveryDialog({ trigger, defaultOpen = false, onOpenChange, bannerImage }) {
  const [open, setOpen] = useState(defaultOpen);
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    description: "",
    address: "",
    location: "",
    image: "",
  });
  const [loadingGps, setLoadingGps] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);

  const handleOpen = (v) => {
    setOpen(v);
    onOpenChange?.(v);
    if (!v) setSuccess(null);
  };

  const captureGps = () => {
    if (!navigator.geolocation) {
      toast.error("جهازك لا يدعم تحديد الموقع");
      return;
    }
    setLoadingGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setForm((f) => ({ ...f, location: `${latitude.toFixed(6)},${longitude.toFixed(6)}` }));
        toast.success("تم تحديد موقعك ✅");
        setLoadingGps(false);
      },
      (err) => {
        toast.error(err?.message || "تعذّر تحديد الموقع — أدخل العنوان يدوياً");
        setLoadingGps(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const pickImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("يرجى اختيار صورة"); return; }
    if (file.size > MAX_IMG_BYTES) { toast.error("الصورة أكبر من 800KB — التقط بجودة أقل"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: String(reader.result || "") }));
    reader.onerror = () => toast.error("تعذّر قراءة الصورة");
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.customer_name.trim()) { toast.error("الاسم مطلوب"); return; }
    if (!form.customer_phone.trim()) { toast.error("رقم الهاتف مطلوب"); return; }
    if (!form.description.trim()) { toast.error("اكتب تفاصيل الطلب"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/free-delivery", form);
      saveMyRequestId(data.id);
      setSuccess(data);
      toast.success("تم إرسال طلبك — سيتواصل معك السائق قريباً 🚴");
      // Reset for next time
      setForm({ customer_name: "", customer_phone: "", description: "", address: "", location: "", image: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل إرسال الطلب");
    } finally {
      setSaving(false);
    }
  };

  const defaultTrigger = (
    <button
      type="button"
      className="w-full group relative overflow-hidden rounded-[40px] px-5 py-4 sm:px-6 sm:py-5 text-start shadow-xl shadow-orange-950/40 transition-transform hover:scale-[1.01] active:scale-[0.99] ring-1 ring-orange-200/25"
      style={{ background: "linear-gradient(135deg, #f97316 0%, #ef4444 55%, #db2777 100%)" }}
      data-testid="free-delivery-cta"
    >
      {/* Ambient glows */}
      <div className="absolute -end-6 -top-8 size-40 rounded-full bg-white/20 blur-3xl" />
      <div className="absolute -start-10 -bottom-10 size-48 rounded-full bg-yellow-200/25 blur-3xl" />

      <div className="relative flex items-center gap-4">
        {/* Text on the right (start in RTL) */}
        <div className="flex-1 min-w-0 text-end">
          <div className="text-[11px] sm:text-xs font-semibold text-orange-50 tracking-wide">
            خدمة داخل هود
          </div>
          <div className="text-xl sm:text-2xl font-black text-white mt-0.5 leading-tight drop-shadow">
            طلب دلفري حرّ 🛵
          </div>
          <div className="text-[11px] sm:text-sm text-orange-50/95 mt-0.5">
            تحتاج شيء يوصلك؟ اطلب سائقاً حراً الآن
          </div>
        </div>

        {/* Icon on the left (end in RTL) */}
        <div className="size-20 sm:size-24 rounded-2xl bg-white/15 backdrop-blur-sm ring-1 ring-white/25 flex items-center justify-center shrink-0 overflow-hidden shadow-inner">
          {bannerImage ? (
            <img
              src={bannerImage}
              alt="دلفري حر"
              className="w-full h-full object-contain drop-shadow-[0_4px_8px_rgba(0,0,0,0.4)]"
            />
          ) : (
            <div className="relative flex items-center justify-center w-full h-full bg-gradient-to-br from-white/25 to-orange-200/15">
              <Bike className="size-10 text-white drop-shadow-lg" strokeWidth={2.25} />
            </div>
          )}
        </div>
      </div>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent dir="rtl" className="max-w-md max-h-[92vh] overflow-y-auto">
        {success ? (
          <div className="text-center py-6" data-testid="free-delivery-success">
            <div className="mx-auto size-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="size-10 text-emerald-600" />
            </div>
            <DialogTitle className="text-xl">تم إرسال طلبك بنجاح</DialogTitle>
            <p className="text-sm text-muted-foreground mt-2 mb-4">
              رقم الطلب: <span className="font-mono font-bold text-foreground dir-ltr">#{success.id?.slice(-6)}</span>
              <br />
              سيصل الإشعار الآن لجميع السائقين المتاحين، وسيتواصل معك أول سائق يقبل الطلب على رقمك:
              <br />
              <span className="font-bold text-foreground dir-ltr">{success.customer_phone}</span>
            </p>
            <p className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800">
              💡 نصيحة: أبقِ هاتفك مفتوحاً — قد يتصل بك السائق للتأكيد قبل الوصول
            </p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <Button asChild variant="outline" className="w-full" data-testid="free-delivery-track-link">
                <Link to="/my-free-deliveries" onClick={() => handleOpen(false)}>
                  📊 طلباتي
                </Link>
              </Button>
              <Button onClick={() => handleOpen(false)} className="w-full" data-testid="free-delivery-close">
                حسناً
              </Button>
            </div>
          </div>
        ) : (
          <>
            <DialogTitle className="flex items-center gap-2">
              <Bike className="size-5 text-orange-600" /> طلب دلفري حرّ
            </DialogTitle>
            <p className="text-xs text-muted-foreground -mt-1 mb-1">
              املأ التفاصيل وسيصلك أقرب سائق متاح مباشرة.
            </p>

            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs flex items-center gap-1"><UserIcon className="size-3" /> الاسم</Label>
                <Input
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  placeholder="اسمك الكامل"
                  data-testid="free-delivery-name"
                />
              </div>

              <div>
                <Label className="text-xs flex items-center gap-1"><Phone className="size-3" /> رقم الهاتف</Label>
                <Input
                  type="tel"
                  inputMode="tel"
                  dir="ltr"
                  value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                  placeholder="07XX XXX XXXX"
                  data-testid="free-delivery-phone"
                />
              </div>

              <div>
                <Label className="text-xs">تفاصيل الطلب</Label>
                <Textarea
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="مثال: أوصل قطعة غيار من محل الشراكسة إلى منطقة القدس — كارتون صغير"
                  data-testid="free-delivery-description"
                />
              </div>

              <div>
                <Label className="text-xs flex items-center gap-1">
                  <ImagePlus className="size-3" /> صورة الطلب (اختياري)
                </Label>
                <p className="text-[10px] text-muted-foreground mb-1">مثال: وصفة طبية، صورة قطعة الغيار، قائمة تسوق</p>
                {form.image ? (
                  <div className="relative rounded-lg overflow-hidden border border-orange-200 dark:border-orange-800">
                    <img src={form.image} alt="طلبك" className="w-full max-h-40 object-contain bg-muted" />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, image: "" })}
                      className="absolute top-1 end-1 size-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                      data-testid="free-delivery-image-remove"
                    >
                      <X className="size-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 w-full h-16 rounded-lg border-2 border-dashed border-orange-300 dark:border-orange-800 text-orange-700 dark:text-orange-300 cursor-pointer hover:bg-orange-50/50 dark:hover:bg-orange-950/20 transition text-sm font-semibold" data-testid="free-delivery-image-label">
                    <ImagePlus className="size-5" />
                    اختر صورة
                    <input type="file" accept="image/*" capture="environment" onChange={pickImage} className="hidden" data-testid="free-delivery-image-input" />
                  </label>
                )}
              </div>

              <div>
                <Label className="text-xs flex items-center gap-1"><MapPin className="size-3" /> العنوان (اختياري)</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="مثال: حي القدس - شارع 5 - مقابل الجامع"
                  data-testid="free-delivery-address"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">موقعك على الخريطة</Label>
                  {form.location && (
                    <span className="text-[10px] text-emerald-700 dark:text-emerald-400 font-bold">✓ محدد</span>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={captureGps}
                  disabled={loadingGps}
                  className="w-full gap-2"
                  data-testid="free-delivery-gps"
                >
                  {loadingGps ? <Loader2 className="size-4 animate-spin" /> : <Navigation className="size-4" />}
                  {form.location ? "إعادة تحديد الموقع" : "تحديد موقعي تلقائياً (GPS)"}
                </Button>
              </div>

              <Button
                onClick={submit}
                disabled={saving}
                className="w-full h-12 text-base font-bold bg-orange-600 hover:bg-orange-700 text-white gap-2"
                data-testid="free-delivery-submit"
              >
                {saving ? <Loader2 className="size-5 animate-spin" /> : <Bike className="size-5" />}
                {saving ? "جاري الإرسال..." : "إرسال الطلب للسائقين"}
              </Button>

              <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
                🔒 معلوماتك تُشارك فقط مع السائق الذي يقبل طلبك
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
